import { supabase } from "./supabase";
import { AnalysisResult, OrderFlowResult, TechnicalAnalysisResult, CotAnalysisResult } from "../types";

const invokeAnalyzer = async (pair: string, reportType: string, skipCache: boolean = false) => {
  // We invoke the 'swift-service' Edge Function
  // We must pass the user's Session token automatically via Supabase Auth
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Authentication required");

  // Use native fetch to get the exact error body (supabase.functions.invoke hides 400 errors)
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/swift-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': anonKey
      },
      body: JSON.stringify({ pair, reportType, skipCache })
    });

    // We try to parse the JSON response from the backend
    const data = await res.json();

    if (!res.ok) {
      if (res.status === 403 && data.error === "FREE_TIER_LOCKED") {
        // Return the generated dummy data so the UI can render it and apply the blur
        return data.dummyData;
      }
      if (res.status === 403 && data.error === "LIMIT_REACHED") {
        throw new Error("LIMIT_REACHED");
      }
      throw new Error(data.error || `Server responded with status: ${res.status}`);
    }

    return data;
  } catch (err: any) {
    console.error("Fetch Error:", err);
    throw new Error(`Edge Function Error: ${err.message || JSON.stringify(err)}`);
  }
};

export const analyzeForexPair = async (pair: string, skipCache: boolean = false): Promise<AnalysisResult> => {
  return await invokeAnalyzer(pair, 'FUNDAMENTAL', skipCache);
};

export const analyzeOrderFlow = async (pair: string, skipCache: boolean = false): Promise<OrderFlowResult> => {
  return await invokeAnalyzer(pair, 'ORDER_FLOW', skipCache);
};

export const analyzeTechnicalTrend = async (pair: string, skipCache: boolean = false): Promise<TechnicalAnalysisResult> => {
  return await invokeAnalyzer(pair, 'CHART', skipCache);
};

export const analyzeCotReport = async (pair: string, skipCache: boolean = false): Promise<CotAnalysisResult> => {
  return await invokeAnalyzer(pair, 'COT', skipCache);
};